import { GraphQLClient, gql } from "graphql-request";

const shopifyDomain = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const storefrontAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;

// Set up GraphQL client
const client = new GraphQLClient(`https://${shopifyDomain}/api/2024-04/graphql.json`, {
  headers: {
    "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
  },
});

// GraphQL query to fetch collections
const GET_COLLECTIONS_QUERY = gql`
  query GetCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          handle
          description
          image {
            src: url
          }
        }
      }
    }
  }
`;

// Function to fetch categories (collections in Shopify)
export const fetchCategories = async () => {
  try {
    const data = await client.request(GET_COLLECTIONS_QUERY, { first: 5 });
    return data.collections.edges.map((edge) => edge.node);
  } catch (err) {
    console.log("There was an error fetching the categories", err);
  }
};


const GET_PRODUCTS_BY_CATEGORY_HANDLE = gql`
query GetProductsByCollection($handle: String!) {
  collectionByHandle(handle: $handle) {
    title
    products(first: 20) {
      edges {
        node {
          id
          title
          description
          handle
          images(first: 1) {
            edges {
              node {
                src: url
                altText
              }
            }
          }
          variants(first: 5) {
            edges {
              node {
                id
                title
                sku
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          metafields(identifiers: [
            { namespace: "custom", key: "days_notice" },
            { namespace: "custom", key: "storage" },
            { namespace: "custom", key: "cooking_instructions" },
            { namespace: "custom", key: "ingredients" }
          ]) {
            id
            namespace
            key
            value
          }
        }
      }
    }
  }
}
`;

export const fetchItemsByCategoryHandle = async (categoryHandle) => {
  try {
    const data = await client.request(GET_PRODUCTS_BY_CATEGORY_HANDLE, { handle: categoryHandle });
    return data.collectionByHandle?.products.edges.map(edge => edge.node) || [];
  } catch (err) {
    console.error('There was an error fetching the items', err);
  }
};

const GET_PRODUCT_BY_HANDLE = gql`
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      tags
      images(first: 1) {
        edges {
          node {
            src: url
            altText
          }
        }
      }
      variants(first: 5) {
        edges {
          node {
            id
            title
            sku
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      metafields(identifiers: [
        { namespace: "custom", key: "days_notice" },
        { namespace: "custom", key: "storage" },
        { namespace: "custom", key: "cooking_instructions" },
        { namespace: "custom", key: "ingredients" }
      ]) {
        id
        namespace
        key
        value
      }
    }
  }
`;

export const fetchItem = async (itemHandle) => {
  try {
    const data = await client.request(GET_PRODUCT_BY_HANDLE, { handle: itemHandle });
    return data.productByHandle || null; // Return the product object or null if not found
  } catch (err) {
    console.log('There was an error retrieving the item', err);
  }
};


//TODO: Add encryption on the locally stored basketId
const CREATE_BASKET_MUTATION = gql`
  mutation CreateCart {
    cartCreate {
      cart {
        id
      }
    }
  }
  `;

export const createBasket = async () => {
  try {

    const response = await client.request(CREATE_BASKET_MUTATION);
    const basketId = response.cartCreate.cart;
    // Store basketid locally for later use
    localStorage.setItem('basketId', basketId);
    
    console.log(basketId)
    return basketId;
  } catch (err) {
    console.error('Error creating basket:', err);
  }
};


const GET_BASKET_QUERY = gql`
  query GetCart($id: ID!) {
    cart(id: $id) {
      id
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                price {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                product {
                  title
                  images(first: 1) {
                    edges {
                      node {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      estimatedCost {
        subtotalAmount {
          amount
          currencyCode
        }
        totalAmount {
          amount
          currencyCode
        }
      }
    }
  }
`;

export const fetchBasket = async (basketId) => {
  try {
    const basket = await client.request(GET_BASKET_QUERY, { id: basketId });
    return basket?.cart || null;
  } catch (err) {
    console.log('Error fetching the basket:', err);
  }
};

const ADD_TO_BASKET_MUTATION = gql`
  mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const handleAddToBasket = async (basketId, quantity, variantId) => {
  try {
    const lines = [{ merchandiseId: variantId, quantity }]; 
    const basket = await client.request(ADD_TO_BASKET_MUTATION, { cartId: basketId, lines });

    if (basket.cartLinesAdd.userErrors.length > 0) {
      console.error('Error adding to basket:', basket.cartLinesAdd.userErrors);
      return null;
    }

    return basket.cartLinesAdd.cart;
  } catch (err) {
    console.log('Error adding item to basket:', err);
  }
};


const UPDATE_BASKET_QUANTITY_MUTATION = gql`
  mutation UpdateCartItemQuantity($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) {
          edges {
            node {
              id
              quantity
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const handleUpdateBasketItemQuantity = async (basketId, lineId, quantity) => {
  try {
    const lines = [{ id: lineId, quantity }];
    const basket = await client.request(UPDATE_BASKET_QUANTITY_MUTATION, { cartId: basketId, lines });

    if (basket.cartLinesUpdate.userErrors.length > 0) {
      console.error('Error updating basket item quantity:', basket.cartLinesUpdate.userErrors);
      return null;
    }

    return basket.cartLinesUpdate.cart;
  } catch (err) {
    console.log('Error updating basket item quantity:', err);
  }
};


const REMOVE_FROM_BASKET_MUTATION = gql`
  mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        lines(first: 100) {
          edges {
            node {
              id
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const handleRemoveFromBasket = async (basketId, lineId) => {
  try {
    const lineIds = [lineId];
    const basket = await client.request(REMOVE_FROM_BASKET_MUTATION, { cartId: basketId, lineIds });

    if (basket.cartLinesRemove.userErrors.length > 0) {
      console.error('Error removing from basket:', basket.cartLinesRemove.userErrors);
      return null;
    }

    return basket.cartLinesRemove.cart;
  } catch (err) {
    console.log('Error removing item from basket:', err);
  }
};


export const handleEmptyBasket = async (basketId) => {
  try {
    const basket = await fetchBasket(basketId);
    const lineIds = basket.lines.edges.map(edge => edge.node.id);

    if (lineIds.length > 0) {
      const clearedBasket = await client.request(REMOVE_FROM_BASKET_MUTATION, { cartId: basketId, lineIds });
      return clearedBasket.cartLinesRemove.cart;
    } else {
      console.log('Basket is already empty');
      return basket;
    }
  } catch (err) {
    console.log('Error emptying the basket:', err);
  }
};


const BASKET_ATTRIBUTES_UPDATE_MUTATION = gql`
  mutation CartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        id
        attributes {
          key
          value
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const updateBasketAttributes = async (basketId, attributes) => {
  try {
    const response = await client.request(BASKET_ATTRIBUTES_UPDATE_MUTATION, {
      cartId: basketId,
      attributes,
    });

    if (response.cartAttributesUpdate.userErrors.length > 0) {
      console.error("Error updating cart attributes:", response.cartAttributesUpdate.userErrors);
      return null;
    }

    return response.cartAttributesUpdate.cart;
  } catch (err) {
    console.error("Error updating cart attributes:", err);
  }
};


const UPDATE_BUYER_IDENTITY_MUTATION = gql`
  mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        buyerIdentity {
          email
          phone
          deliveryAddressPreference {
            address1
            address2
            city
            provinceCode
            zip
            countryCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;


export const updateBuyerIdentity = async (basketId, email, phone, deliveryAddress) => {
  try {
    const buyerIdentity = {
      email,
      phone,
      countryCode: "GB",
      deliveryAddressPreference: {
        address1: deliveryAddress.line1,
        address2: deliveryAddress.line2,
        city: deliveryAddress.town,
        zip: `${deliveryAddress.outwardCode} ${deliveryAddress.inwardCode}`
      }
    };

    const response = await client.request(UPDATE_BUYER_IDENTITY_MUTATION, {
      cartId: basketId,
      buyerIdentity,
    });

    if (response.cartBuyerIdentityUpdate.userErrors.length > 0) {
      console.error("Error updating buyer identity:", response.cartBuyerIdentityUpdate.userErrors);
      return null;
    }

    return response.cartBuyerIdentityUpdate.cart;
  } catch (err) {
    console.error("Error updating buyer identity:", err);
  }
};

const GET_BASKET_CHECKOUT_URL_QUERY = gql`
  query GetCartCheckoutUrl($id: ID!) {
    cart(id: $id) {
      checkoutUrl
    }
  }
`;

export const fetchBasketCheckoutUrl = async (basketId) => {
  try {
    const data = await client.request(GET_BASKET_CHECKOUT_URL_QUERY, { id: basketId });
    return data.cart.checkoutUrl;
  } catch (err) {
    console.log("Error fetching checkout URL:", err);
  }
};


